import React, { useState, useEffect, useRef, useContext } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import * as MemorandumService from '../axios_services/memorandum.service';
import ModalMemorandum from '../components/Modal/ModalMemorandum';
import Loader from '../components/Loader/Loader';
import { ButtonInsert, ButtonSearch } from '../components/Buttons/Buttons';
import ButtonUpdate from '../components/Buttons/ButtonUpdate';
import ButtonDelete from '../components/Buttons/ButtonDelete';
import { FileMinus } from "lucide-react";
import ArchivoService from '../axios_services/archivos.service';

const now = new Date();
const mesActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

const Memorandum = () => {
    const [state] = useContext(AppContext);
    const [memorandums, setMemorandums] = useState([]);
    const [loading, setLoading] = useState(false);
    const [mesInput, setMesInput] = useState(mesActual);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selected, setSelected] = useState(null);
    const initialFetch = useRef(false);

    const PERFILES_PERMITIDOS = [1, 3, 5, 6, 9, 11, 21, 22, 23, 24, 25, 26];
    const PERFILES_EMISOR = [5, 22, 26];
    const PERFILES_ACCIONES = [5, 8, 22, 26];
    const PERFILES_VER_TODOS = [5, 8, 9, 22, 26];

    const currentUserId = Number(state.user?.usuario_id);
    const currentUserProfileId = Number(state.user?.perfil_id);
    const puedeVerAcciones = PERFILES_ACCIONES.includes(currentUserProfileId);
    const puedeVerTodos = PERFILES_VER_TODOS.includes(currentUserProfileId);

    const personalList = (state.user?.personal || [])
        .filter(p => PERFILES_PERMITIDOS.includes(Number(p.perfil_id)))
        .sort((a, b) => (a.nombre_completo_usuario || '').localeCompare(b.nombre_completo_usuario || ''));

    const emisorList = (state.user?.personal || [])
        .filter(p => PERFILES_EMISOR.includes(Number(p.perfil_id)))
        .sort((a, b) => (a.nombre_completo_usuario || '').localeCompare(b.nombre_completo_usuario || ''));

    useEffect(() => {
        if (!initialFetch.current) {
            fetchData();
            initialFetch.current = true;
        }
    }, []);

    const fetchData = async (mes = mesInput) => {
        setLoading(true);
        try {
            const res = await MemorandumService.getMemorandum(mes);
            const data = res.data || [];
            if (puedeVerTodos) {
                setMemorandums(data);
            } else {
                setMemorandums(data.filter(item => Number(item.colaborador_id) === currentUserId));
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => fetchData(mesInput);

    const handleCreate = () => {
        setSelected(null);
        setIsModalOpen(true);
    };

    const handleEdit = (item) => {
        setSelected(item);
        setIsModalOpen(true);
    };

    const handleVerDocumentoMemorandum = async (memorandum_id) => {
        try {
            const documento = await ArchivoService.getDocumentoMemorandum(memorandum_id);
            if (documento && documento.url_completa) {
                window.open(documento.url_completa, "_blank", "noopener,noreferrer");
            } else {
                alert("Este registro no tiene un documento adjunto.");
            }
        } catch (error) {
            alert("Ocurrió un error al intentar abrir el documento.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar este memorándum?')) return;
        try {
            setLoading(true);
            await MemorandumService.deleteMemorandum(id);
            fetchData(mesInput);
        } catch (err) {
            alert('Error al eliminar el memorándum.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="caja-flujo-container">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>REGISTRO DE MEMORANDUMS</h1>
            </div>

            <div className="filtros" style={{ marginBottom: '10px' }}>
                <div>
                    <label>Mes:</label>
                    <input
                        type="month"
                        value={mesInput}
                        onChange={e => setMesInput(e.target.value)}
                    />
                </div>
            </div>

            <div className="mb-4">
                <ButtonSearch onClick={handleSearch} isLoading={loading} />
            </div>

            {puedeVerAcciones && (
                <div className="nuevo-movimiento-section mb-3">
                    <ButtonInsert onClick={handleCreate} />
                </div>
            )}

            <div className="table-container">
                <Table hover size="sm" className="mb-0">
                    <thead>
                        <tr>
                            <th className="bg-gray-700 text-white uppercase">Código</th>
                            <th className="bg-gray-700 text-white uppercase">Fecha</th>
                            <th className="bg-gray-700 text-white uppercase">Asunto</th>
                            <th className="bg-gray-700 text-white uppercase">Emisor</th>
                            <th className="bg-gray-700 text-white uppercase">Dirigido a</th>
                            <th className="bg-gray-700 text-white uppercase text-center">Documento</th>
                            {puedeVerAcciones && <th className="bg-gray-700 text-white uppercase">Acciones</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {memorandums.length > 0 ? memorandums.map(item => (
                            <tr key={item.memorandum_id}>
                                <td>{item.codigo}</td>
                                <td>{item.fecha_registro ? item.fecha_registro.substring(0, 10) : '—'}</td>
                                <td>{item.asunto}</td>
                                <td>{item.nombre_emisor}</td>
                                <td>{item.nombre_colaborador || '—'}</td>
                                <td>
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        {item.tiene_documento === 1 || item.tiene_documento === '1' || item.tiene_documento === true ? (
                                            <button
                                                onClick={() => handleVerDocumentoMemorandum(item.memorandum_id)}
                                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: '#3cb167' }}
                                                title="Ver documento"
                                            >
                                                <FileMinus size={20} />
                                            </button>
                                        ) : (
                                            <FileMinus size={20} style={{ color: '#f87171', opacity: 0.5 }} title="Sin documento" />
                                        )}
                                    </div>
                                </td>
                                {puedeVerAcciones && (
                                    <td>
                                        <div className="acciones-container">
                                            <ButtonUpdate onClick={() => handleEdit(item)} />
                                            <ButtonDelete onClick={() => handleDelete(item.memorandum_id)} />
                                        </div>
                                    </td>
                                )}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={puedeVerAcciones ? 7 : 6} className="text-center text-gray-500 py-4">No hay registros.</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalMemorandum
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setSelected(null); }}
                memorandum={selected}
                onRefresh={() => fetchData(mesInput)}
                personalList={personalList}
                emisorList={emisorList}
                currentUserId={currentUserId}
            />
        </div>
    );
};

export default Memorandum;
